import { useCallback, useEffect, useState } from "react";
import AdminStatePanel from "../../../layout/AdminStatePanel";
import { useSiataSantaElenaRadarReplay } from "../../../../../../hooks/useSiataSantaElenaRadarReplay.js";
import {
  createSiataSantaElenaArchiveIncident,
  fetchSiataSantaElenaArchiveIncidents,
  fetchSiataSantaElenaArchiveSummary,
} from "../../../../../../weather/siataSantaElenaRadarArchiveApi.js";

function toLocalInputValue(date) {
  const value = date instanceof Date ? date : new Date(date);
  const offset = value.getTimezoneOffset();
  const local = new Date(value.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
}

const WeatherSiataReplayPanel = ({ canManage, onToast }) => {
  const [summary, setSummary] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [incidentTitle, setIncidentTitle] = useState("");
  const [startInput, setStartInput] = useState("");
  const [endInput, setEndInput] = useState("");

  const replay = useSiataSantaElenaRadarReplay({ enabled: canManage });

  const loadSummary = useCallback(async () => {
    if (!canManage) {
      return;
    }

    setLoadingSummary(true);
    try {
      const [summaryPayload, incidentsPayload] = await Promise.all([
        fetchSiataSantaElenaArchiveSummary(),
        fetchSiataSantaElenaArchiveIncidents().catch(() => ({ incidents: [] })),
      ]);
      setSummary(summaryPayload);
      setIncidents(incidentsPayload.incidents || []);
    } catch (error) {
      onToast?.({ type: "error", message: error.message });
    } finally {
      setLoadingSummary(false);
    }
  }, [canManage, onToast]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    if (!summary?.newestFrameTime) {
      return;
    }

    const end = new Date(summary.newestFrameTime);
    const start = new Date(end.getTime() - 2 * 60 * 60 * 1000);
    setStartInput(toLocalInputValue(start));
    setEndInput(toLocalInputValue(end));
  }, [summary?.newestFrameTime]);

  if (!canManage) {
    return null;
  }

  const handleLoadReplay = async () => {
    const start = startInput ? new Date(startInput).toISOString() : null;
    const end = endInput ? new Date(endInput).toISOString() : null;
    await replay.refreshReplayWindow({ start, end, limit: 120 });
  };

  const handleCreateIncident = async () => {
    try {
      await createSiataSantaElenaArchiveIncident({
        title: incidentTitle || "SIATA radar incident review",
        startTimeUtc: startInput ? new Date(startInput).toISOString() : null,
        endTimeUtc: endInput ? new Date(endInput).toISOString() : null,
      });
      onToast?.({ type: "success", message: "Incident archive created." });
      setIncidentTitle("");
      await loadSummary();
    } catch (error) {
      onToast?.({ type: "error", message: error.message });
    }
  };

  return (
    <div className="admin-dash__card admin-dash__card--wide panel-card">
      <div className="admin-section-title-row">
        <h2 className="admin-section-title">SIATA Replay / Incident Review</h2>
        <button
          type="button"
          className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
          onClick={loadSummary}
          disabled={loadingSummary}
        >
          Refresh
        </button>
      </div>

      <p className="muted">
        Historical SIATA radar replay from bounded rolling archive. Local official radar imagery,
        replayed from cached frames.
      </p>
      <p className="weather-alert-card__note">{replay.disclaimer}</p>

      {loadingSummary ? (
        <AdminStatePanel variant="loading" message="Loading archive summary…" />
      ) : null}

      {!loadingSummary && summary ? (
        <div className="weather-siata-replay-summary muted">
          <span>{summary.frameCount || 0} archived frames</span>
          <span> · </span>
          <span>{summary.incidentCount || 0} incidents</span>
          {summary.oldestFrameTime ? (
            <>
              <span> · Oldest </span>
              <span>{new Date(summary.oldestFrameTime).toLocaleString()}</span>
            </>
          ) : null}
        </div>
      ) : null}

      <div className="weather-siata-replay-controls">
        <label>
          <span>Start</span>
          <input type="datetime-local" value={startInput} onChange={(e) => setStartInput(e.target.value)} />
        </label>
        <label>
          <span>End</span>
          <input type="datetime-local" value={endInput} onChange={(e) => setEndInput(e.target.value)} />
        </label>
        <button
          type="button"
          className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
          onClick={handleLoadReplay}
          disabled={replay.isLoading}
        >
          Load replay
        </button>
        {replay.mode === "replay" ? (
          <button
            type="button"
            className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
            onClick={replay.goToLatest}
          >
            Return to live
          </button>
        ) : null}
      </div>

      {replay.error ? <p className="weather-alert-card__note">{replay.error}</p> : null}

      {replay.mode === "replay" && replay.frames.length > 0 ? (
        <div className="weather-siata-replay-player">
          <p>
            <strong>Replay mode</strong> · {replay.sourceLabel} · {replay.trustLabel}
          </p>
          <p className="muted">
            Frame {replay.currentFrameIndex + 1} of {replay.frames.length}
            {replay.currentFrame?.timestampUtc
              ? ` · ${new Date(replay.currentFrame.timestampUtc).toLocaleString()}`
              : ""}
          </p>
          <div className="weather-siata-replay-player__actions">
            <button
              type="button"
              className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
              onClick={replay.isPlaying ? replay.pause : replay.play}
            >
              {replay.isPlaying ? "Pause" : "Play"}
            </button>
            <button
              type="button"
              className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
              onClick={() => replay.goToFrame(Math.max(0, replay.currentFrameIndex - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
              onClick={() =>
                replay.goToFrame(
                  Math.min(replay.frames.length - 1, replay.currentFrameIndex + 1)
                )
              }
            >
              Next
            </button>
          </div>
          <input
            type="range"
            min={0}
            max={Math.max(0, replay.frames.length - 1)}
            value={replay.currentFrameIndex}
            onChange={(event) => replay.goToFrame(Number(event.target.value))}
          />
          {replay.currentFrame?.imageUrl ? (
            <img
              src={replay.currentFrame.imageUrl}
              alt="SIATA radar replay frame"
              className="weather-siata-replay-frame-preview"
            />
          ) : null}
        </div>
      ) : null}

      <div className="weather-siata-replay-incident">
        <h3 className="admin-section-title">Create incident archive</h3>
        <label>
          <span>Title</span>
          <input
            type="text"
            value={incidentTitle}
            onChange={(event) => setIncidentTitle(event.target.value)}
            placeholder="Major rain event — incident review"
          />
        </label>
        <button
          type="button"
          className="admin-dashboard__action-btn admin-dashboard__action-btn--compact"
          onClick={handleCreateIncident}
        >
          Pin window to incident archive
        </button>
      </div>

      {incidents.length > 0 ? (
        <ul className="weather-siata-replay-incident-list">
          {incidents.map((incident) => (
            <li key={incident.id}>
              <strong>{incident.title}</strong>
              <span className="muted">
                {" "}
                · {incident.frameCount} frames · {new Date(incident.createdAt).toLocaleString()}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default WeatherSiataReplayPanel;
